"use client";

import type { CommitmentLevel, MorningBrief } from "@/lib/api";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

export type CarryForwardSummary = NonNullable<MorningBrief["carry_forward_summary"]>;
export type ExecutionPatternHint = NonNullable<CarryForwardSummary["execution_pattern_hint"]>;

export function buildCarryForwardMessage(summary: CarryForwardSummary): string {
  const parts: string[] = [];
  if (summary.tomorrow_count > 0) {
    parts.push(`${summary.tomorrow_count} kept for today`);
  }
  if (summary.later_count > 0) {
    parts.push(`${summary.later_count} moved later`);
  }
  if (summary.made_smaller_count > 0) {
    parts.push(`${summary.made_smaller_count} made smaller`);
  }
  if (summary.not_now_count > 0) {
    parts.push(`${summary.not_now_count} released`);
  }
  if (parts.length === 0) return "Yesterday's carry-forward choices are settled.";
  if (parts.length === 1) return `From yesterday: ${parts[0]}.`;
  if (parts.length === 2) return `From yesterday: ${parts[0]} and ${parts[1]}.`;
  return `From yesterday: ${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)}.`;
}

export function hasTodayCarryForwardAction(summary: CarryForwardSummary): boolean {
  return summary.tomorrow_count > 0 || summary.made_smaller_count > 0;
}

export function hasScheduleCarryForwardAction(summary: CarryForwardSummary): boolean {
  return summary.later_count > 0;
}

export function getCarryForwardPrimaryLabel(summary: CarryForwardSummary, canReviewToday: boolean): string {
  if (canReviewToday && hasTodayCarryForwardAction(summary)) {
    return "Review today";
  }
  if (hasScheduleCarryForwardAction(summary)) {
    return "Plan around this";
  }
  return "Details";
}

export function buildCarryForwardFocusHint(summary: CarryForwardSummary): string | null {
  if (!summary.suggested_focus_title) return null;
  const cleanFocusTitle = sanitizeSurfaceTitle(summary.suggested_focus_title);
  if (!cleanFocusTitle) return null;

  if (summary.suggested_focus_kind === "smaller") {
    return `Start gently with ${cleanFocusTitle}.`;
  }
  if (summary.suggested_focus_kind === "today") {
    return `Start with ${cleanFocusTitle}.`;
  }
  if (summary.suggested_focus_kind === "later") {
    return `When you're ready, reopen ${cleanFocusTitle} on the schedule.`;
  }
  return null;
}

export function buildExecutionPatternHeadline(
  hint: ExecutionPatternHint | null | undefined,
): string | null {
  if (!hint) return null;
  if (hint.headline) return hint.headline;
  if (hint.kind === "must_do_overload") return "Protect one must-do";
  if (hint.kind === "repeated_restart") return "Count one clean pass";
  return "Keep the restart light";
}

export function buildExecutionPatternNextStep(
  hint: ExecutionPatternHint | null | undefined,
): string | null {
  if (!hint) return null;
  if (hint.next_step) return hint.next_step;
  if (hint.kind === "must_do_overload") {
    return "Pick one protected thing to hold today and let the rest stay planned or flexible.";
  }
  if (hint.kind === "repeated_restart") {
    return "Plan one clean pass, then decide whether it needs another round.";
  }
  return "Keep the first block narrow again before deciding on anything bigger.";
}

export function getExecutionPatternCommitmentRecommendation(
  hint: ExecutionPatternHint | null | undefined,
): { level: CommitmentLevel; label: string; message: string } | null {
  if (!hint) return null;
  if (
    hint.recommended_commitment_level &&
    hint.recommended_commitment_label &&
    hint.recommended_commitment_message
  ) {
    return {
      level: hint.recommended_commitment_level,
      label: hint.recommended_commitment_label,
      message: hint.recommended_commitment_message,
    };
  }
  if (hint.kind === "must_do_overload") {
    return {
      level: "planned",
      label: "Recommended: Planned",
      message: "Recent must-dos have been slipping. Keep new work planned unless it truly has to hold today.",
    };
  }
  if (hint.kind === "repeated_restart") {
    return {
      level: "planned",
      label: "Recommended: Planned",
      message: "Keep this planned until one clean pass lands, then protect it later only if it still needs to hold.",
    };
  }
  return null;
}

function normalizeFocusText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function matchesCarryForwardFocusTitle(
  title: string,
  summary: CarryForwardSummary | null | undefined,
): boolean {
  if (!summary?.suggested_focus_title) return false;
  const normalizedTitle = normalizeFocusText(sanitizeSurfaceTitle(title));
  const normalizedFocus = normalizeFocusText(sanitizeSurfaceTitle(summary.suggested_focus_title));
  if (!normalizedTitle || !normalizedFocus) return false;
  return (
    normalizedFocus.includes(normalizedTitle) ||
    normalizedTitle.includes(normalizedFocus)
  );
}

export function getCarryForwardFocusBadgeLabel(
  summary: CarryForwardSummary | null | undefined,
): string | null {
  if (!summary?.suggested_focus_title) return null;
  if (summary.suggested_focus_kind === "later") return "Reopen here";
  if (summary.suggested_focus_kind === "today" || summary.suggested_focus_kind === "smaller") {
    return "Start here";
  }
  return null;
}
